import { atom } from 'jotai';

import { purchaseLoadUpdateAtom } from '../_graphql/mutationPurchaseLoadLineItemsUpdate';

import { createAutoSaveFormAtoms } from './createAutoSaveFormAtoms.state';
import {
  $inboundTicketLoads,
  $scaleTicketDetail,
  $scaleTicketId,
} from './scaleTicketPro.state';

export type TicketDetailsFormState = {
  description: string | null;
  receivingTicketNumber: string | null;
  truckNumber: string | null;
  trailerNumber: string | null;
  driverName: string | null;
  containerNumber: string | null;
  sealNumber: string | null;
  bolNumber: string | null;
  vehicleLicensePlate: string | null;
};

export const DEFAULT_DETAILS_FORM: TicketDetailsFormState = {
  description: null,
  receivingTicketNumber: null,
  truckNumber: null,
  trailerNumber: null,
  driverName: null,
  containerNumber: null,
  sealNumber: null,
  bolNumber: null,
  vehicleLicensePlate: null,
};

type TicketDetailsServerData = {
  description: string | null;
  receivingTicketNumber: string | null;
  truckNumber: string | null;
  trailerNumber: string | null;
  driverName: string | null;
  containerNumber: string | null;
  sealNumber: string | null;
  bolNumber: string | null;
  vehicleLicensePlate: string | null;
};

const atoms = createAutoSaveFormAtoms<
  TicketDetailsFormState,
  TicketDetailsServerData
>({
  defaultForm: DEFAULT_DETAILS_FORM,
  fromServerData: (ticket) => ({
    description: ticket.description,
    receivingTicketNumber: ticket.receivingTicketNumber,
    truckNumber: ticket.truckNumber,
    trailerNumber: ticket.trailerNumber,
    driverName: ticket.driverName,
    containerNumber: ticket.containerNumber,
    sealNumber: ticket.sealNumber,
    bolNumber: ticket.bolNumber,
    vehicleLicensePlate: ticket.vehicleLicensePlate,
  }),
  createMutation: (onSuccess, onError) => {
    const $mut = purchaseLoadUpdateAtom({
      onSuccess: (get, set, { params }) => onSuccess(get, set, params.id),
      onError: (get, set, { params }) => onError(get, set, params.id),
    });
    return atom(
      null,
      (
        _get,
        set,
        params: { ticketId: string; form: TicketDetailsFormState },
      ) => {
        set($mut, { id: params.ticketId, ...params.form });
      },
    );
  },
  refetchOnSuccess: (get, ticketId) => {
    if (get($scaleTicketId) === ticketId) {
      get($scaleTicketDetail).refetch();
    }
    get($inboundTicketLoads).refetch();
  },
});

// 🔵🔵🔵 LOCAL STATE - Current form values per ticket
export const $ticketDetailsFormById = atoms.$formById;
// 🔵🔵🔵 LOCAL STATE - Last synced server snapshot per ticket
export const $ticketDetailsBaselineById = atoms.$baselineById;
// 🟠🟠🟠 ACTION - Update a single field and trigger debounced auto-save
export const $updateTicketDetailsField = atoms.$updateField;
// 🟠🟠🟠 ACTION - Sync server data into local form state (cancels pending debounce)
export const $syncTicketDetailsForm = atoms.$syncForm;
// 🟠🟠🟠 ACTION - Immediately save pending changes (cancels debounce)
export const $flushTicketDetailsAutoSave = atoms.$flushAutoSave;
// 🟠🟠🟠 ACTION - Cancel pending debounce without saving
export const $cancelTicketDetailsAutoSave = atoms.$cancelAutoSave;
export const $cleanupTicketDetailsForm = atoms.$cleanupForm;
